import $ from 'jquery';
type Job = { description?: string, ats: string, id: string, title: string, location: string, type: string, date: string } & Record<string, string>;

$(function() {
	function debounce<C, T extends (this: C, ...args: any[]) => void>(func: T, wait = 500, immediate = false) {
		let timeout: ReturnType<typeof window.setTimeout>|null = null;
		return function (this: any, ...args: any[]) {
			const context = this;
			const later = function () {
				timeout = null;
				if (!immediate) func.apply(context, args);
			};
			const callNow = immediate && !timeout;
			if (timeout !== null) {
				clearTimeout(timeout);
			}
			timeout = setTimeout(later, wait);
			if (callNow) func.apply(context, args);
		};
	}

	document.querySelectorAll('.frogmo-jobs-list').forEach(container => {
		const labelMappings: Record<string, string> = {};
		const rawLabelMappings = container.getAttribute("data-filterable-label-mappings");
		if (rawLabelMappings) {
			for (const {"original-label": original, "displayed-label": replacement} of JSON.parse(rawLabelMappings)) {
				labelMappings[original] = replacement;
			}
		}
		const jobURLTemplate = container.getAttribute("data-job-url-template") ?? "";
		const jobListEntryTemplateRootContainer = container.querySelector("template");
		if (!jobListEntryTemplateRootContainer) {
			console.error("Unable to find the Job List Entry template")
			return;
		}
		const jobListEntryTemplateRoot = jobListEntryTemplateRootContainer.content;

		// toggle filters open and closed on mobile view
		const toggleMobileFiltersView = () => {
			const mobileJobFiltersButton = container.querySelector<HTMLElement>('.job-mobile-filters-toggle--button');
			if (!mobileJobFiltersButton) {
				return;
			}

			if (container.classList.toggle('search--open')) {
				mobileJobFiltersButton.textContent = 'View Results';
			} else {
				mobileJobFiltersButton.textContent = 'Open Filters';
			}
		}

		// mobile filters
		for (const toggleItem of container.querySelectorAll('.job-mobile-filters-toggle')) {
			toggleItem.addEventListener('click', toggleMobileFiltersView);
		}

		const makePlural = (word: string) => word.endsWith('y') ? word.substring(0, word.length - 1) + "ies" : word + 's';

		const filterablePropertyNames = [...container.querySelectorAll(".search__inner .searchFields ul")].map(f => f.id);
		const filterableProperties: Map<string, {
			singularName: string,
			pluralName: string,
			queryName: string,
			elements: HTMLUListElement,
			selected: unknown[]
		}> = new Map();
		for (const filterablePropertyName of filterablePropertyNames) {
			const pluralName = makePlural(filterablePropertyName);

			filterableProperties.set(filterablePropertyName, {
				singularName: filterablePropertyName,
				pluralName,
				queryName: filterablePropertyName.substring(0, 1) + '[]',
				elements: document.getElementById(filterablePropertyName) as HTMLUListElement,
				selected: []
			});
		}
		let jobs: Job[] = [];
		let lastKeyword: string|undefined = undefined;

		const loadingHTML = '<div class="loading-container"><div class="loadingio-spinner-ripple-syqd1gm9yb"' +
			'><div class="ldio-x23zodqfo3h"><div></div><div></div></div></div></div>';

		let searchJobs = debounce(apiRequest, 500);

		container.querySelector<HTMLElement>(".load-more")?.addEventListener('click', () => loadJobs());
		container.querySelector<HTMLInputElement>("input.keyword")?.addEventListener("keyup", searchJobs);

		for (let value of filterableProperties.values()) {
			value.elements.innerHTML = loadingHTML;
		}
		searchJobs();

		function buildJobElement(job: Job) {
			let url = jobURLTemplate;
			if (url) {
				for (const [key, value] of Object.entries(job)) {
					url = url.replaceAll(`{{${key}}}`, value);
				}
				let mutableURL: URL;
				try {
					mutableURL = new URL(url);
				} catch (e: any) {
					mutableURL = new URL(window.location.href);
					for (const key  of mutableURL.searchParams.keys()) {
						mutableURL.searchParams.delete(key);
					}
					mutableURL.pathname = url;
				}
				if (container.getAttribute("data-sandbox") === 'true') {
					mutableURL.searchParams.append("sandbox", "");
				}
				if (container.getAttribute("data-url-fragment")) {
					mutableURL.hash = container.getAttribute("data-url-fragment")!;
				}

				if (!mutableURL.pathname.endsWith("/")) {
					mutableURL.pathname = mutableURL.pathname + "/";
				}
				url = mutableURL.toString();
			}
			const jobElement = jobListEntryTemplateRoot.cloneNode(true) as HTMLElement;
			jobElement.querySelectorAll("[data-entity-value]").forEach(element => {
				for (const entityValue of element.getAttribute("data-entity-value")!.split(",")) {
					const [destination, property] = entityValue.split(':');
					let propertyValue: string|null;
					if (property === "url") {
						propertyValue = url;
					} else if (property === "description") {
						let description = (job[property] || "")
							.replaceAll("&nbsp;", ' ') // Deal with potential encoding weirdness
							.replaceAll(/ {2,}/g, ' ') // Strip out chained spaces (HTML won't show them, but they could cause weirdness later on)
							.replaceAll(/<(strong|span)([^>]*)style="[^"]*"([^>]*)>/gi, "<$1$2$3>") // Remove the random inline styling from what are typically "header" tags
							.replaceAll(/<p>(((?!<\/p>).)*?)Duration:(.*?)<\/p>/g, '<p class="duration">$1Duration:$3</p>')
							.trim();
						let oldDescription, passes = 0;

						do {
							oldDescription = description;
							description = description
								.replaceAll(/<([^> ]+)[^>]*>(\s*)<\/\1>/gi, "$2") // Remove tags that exclusively encapsulate whitespace
								.replaceAll(/<\/p>(\s|<\/?br\/?>)+<p>/gi, "</p><p>") // Remove spaces and breaks from between paragraph tags
								.replaceAll(/<p>(\s|<\/?br\/?>)+<\/p>/gi, "") // Remove paragraph tags that exclusively contain newlines
								.replaceAll(/ {2,}/g, ' ') // Strip out any newly-introduced chained spaces
								.trim();
						} while (description.length !== oldDescription.length && (++passes < 20)); // Keep going until we don't change the string, or we've performed 20 passes.
						propertyValue = description;
					} else {
						propertyValue = labelMappings[job[property]] ?? job[property];
					}

					if (!propertyValue) {
						propertyValue = element.getAttribute("data-entity-default-value");
						if (!propertyValue) {
							(element.closest(".job-person-field:not(.job-person-field-has-multiple)") ?? element).remove();
							continue;
						}
					}

					if (destination === '@') {
						element.innerHTML = propertyValue;
					} else {
						element.setAttribute(destination, propertyValue);
					}
				}
			});
			return jobElement;
		}

		function loadJobs(first = false) {
			let next = jobs.splice(0, 20);
			const jobList = container.querySelector<HTMLElement>(".job-container ul.jobs");
			if (jobList !== null) {
				if (first) {
					jobList.innerHTML = "";
				}
				for (let job of next) {
					jobList.appendChild(buildJobElement(job));
				}

				if (next.length === 0) {
					// Message that appears when no jobs are found.
					if (first) {
						const clearUrl = window.location.protocol + '//' + window.location.host + window.location.pathname;
						jobList.innerHTML = `
							<li class="noResults" style="text-align:center; font-weight:700;">
								There are currently no positions with your search criteria. <a href="${clearUrl}" class="noResults-link">Clear Search Criteria</a>
							</li>
						`;
					}
				}
			}

			if (jobs.length === 0) {
				const queryString = window.location.search;
				const urlParams = new URLSearchParams(queryString);
				const queryParamT = urlParams.get('t[]'); // Position Level
				const queryParamS = urlParams.get('s[]'); // Location
				const queryParamC = urlParams.get('c[]'); // Category
				console.log('Query String Param: ', queryParamT, queryParamS, queryParamC);

				// Hide 'load more' button.
				const loadMore = container.querySelector<HTMLElement>(".load-more");
				if (loadMore !== null) {
					loadMore.style.opacity = "0";
					setTimeout(() => loadMore.style.display = "none", 500);
				}
			}
		}

		let loadFromURL = 2;

		function restoreFilterableThings(result: Record<string, Record<string, unknown>>, targetId: string, targetElement: HTMLElement|null) {
			const promises: Promise<boolean>[] = [];
			const urlParams = new URLSearchParams(location.search);
			for (let [filterableThingName, filterableThing] of filterableProperties.entries()) {
				promises.push(new Promise(resolve => {
					const currentlySelected = [];
					if (loadFromURL === 1) {
						currentlySelected.push(...urlParams.getAll(filterableThing.singularName), ...urlParams.getAll(filterableThing.queryName));
					} else {
						const keyword = container.querySelector<HTMLInputElement>("input.keyword");
						if (keyword?.value === lastKeyword && (!targetElement
							|| filterablePropertyNames.indexOf(targetElement.getAttribute('data-thing-type')!) >= filterablePropertyNames.indexOf(filterableThingName))
						) {
							filterableThing.elements.querySelectorAll<HTMLInputElement>("input[checked],#" + targetId).forEach(element => {
								if (element.value && (element.id === targetId ? element.checked : true)) {
									currentlySelected.push(element.value);
								}
							});
						}
					}

					let html = "", i = 1;
					const pluralName = filterableThing.pluralName;
					const things = result[pluralName];
					const thingsKeys = Object.keys(things);

					//   Empty capabilities array
					const foundCapabilities = [];

					const remoteIndex = thingsKeys.findIndex(v => v.toLowerCase() === "remote");
					if (remoteIndex !== -1) {
						thingsKeys.splice(0, 0, thingsKeys.splice(remoteIndex, 1)[0]);
					}

					for (let name of thingsKeys) {
						let displayName;

						// If filterableThing is a Capability then add it to the 'foundCapabilities' array.
						if (filterableThing.singularName === 'capability') {
							foundCapabilities.push(name);
						}

						// Replace 'displayName' if they Match the following.
						if (name === 'Enterprise Finance') {
							displayName = 'Finance';
						} else if (name === 'Enterprise Human Resources') {
							displayName = 'HR/Training';
						} else if (name === 'Enterprise Marketing') {
							displayName = 'Marketing';
						} else if (name === 'Enterprise Operations') {
							displayName = 'IT, Contracts & Legal';
						} else if (name === 'Enterprise Recruiting') {
							displayName = 'Talent Acquisition';
						} else if (name === 'Enterprise Sales') {
							displayName = 'Sales';
						} else {
							displayName = labelMappings[name] ?? name;
						}

						let checked = (currentlySelected.indexOf(name) !== -1 ? 'checked="checked" ' : "");
						const id = `${filterableThing.singularName}-${i}`;
						html += `<li class="test"><label for="${id}"><input id="${id}" type="checkbox" data-thing-type="${filterableThing.singularName}"
									name="${filterableThing.queryName}" value="${name}" ${checked}/> ${displayName} (${things[name]})</label></li>`;
						i++;
					}

					//   List of ALL Capabilities
					if (foundCapabilities.length > 0) {
						console.log('Found Array', foundCapabilities);
					}

					filterableThing.elements.innerHTML = html;

					let inputs = filterableThing.elements.querySelectorAll('input[data-thing-type]');
					for (let input of inputs) {
						input.addEventListener("change", searchJobs);
					}
					resolve(true);
				}));
			}
			return Promise.all(promises).then(r => {
				if (loadFromURL === 1) {
					loadFromURL = 0;
				}
				return r.reduce((a, b) => a && b, true);
			});
		}

		function apiRequest(this: unknown, firstRun = true) {
			let targetId = "does-not-exist";
			if (typeof this === 'object' && this !== null && "getAttribute" in this && typeof this.getAttribute === 'function' && filterableProperties.has(this.getAttribute('data-thing-type')!)) {
				targetId = this.getAttribute('id');
			}
			const targetElement = document.getElementById(targetId);
			const xmlHttp = new XMLHttpRequest();
			let url = container.getAttribute("data-api-url-suffix");

			for (let i = targetElement ? filterablePropertyNames.indexOf(targetElement.getAttribute('data-thing-type')!) + 1 : 0; i < filterablePropertyNames.length; i++) {
				filterableProperties.get(filterablePropertyNames[i])!.elements.innerHTML = loadingHTML;
			}

			xmlHttp.onreadystatechange = function () {
				if (this.readyState === 4 && this.status === 200) {
					let result;
					try {
						result = JSON.parse(this.responseText) as {jobs: Job[]}&Record<Exclude<string, 'jobs'>, Record<string, unknown>>;
					} catch (e: any) {
						if (firstRun) {
							console.error("Bullhorn randomly failed"); // Bullhorn just randomly fails sometimes, so we simply wait a second and then try again.
							setTimeout(() => apiRequest(false), 1000);
							return;
						}
						throw e;
					}

					jobs = result.jobs;

					const jobCount = container.querySelector<HTMLElement>(".job-count");
					if (jobCount) {
						jobCount.innerHTML = `(${jobs.length})`;
					}

					restoreFilterableThings(result, targetId, targetElement).then(() => {
						const loadMore = container.querySelector<HTMLElement>(".load-more");
						if (jobs.length > 0 && loadMore !== null) {
							loadMore.style.display = "initial";
							loadMore.style.opacity = "1";
						}

						lastKeyword = container.querySelector<HTMLInputElement>("input.keyword")?.value ?? undefined;

						loadJobs(true);
					});
				}
			};

			let query = "";
			const keyword = container.querySelector<HTMLInputElement>("input.keyword");
			if (loadFromURL === 2) {
				loadFromURL = 1;
				const initialParams = new URLSearchParams(location.search);
				if (keyword) {
					keyword.value = initialParams.get('k') || "";
					if (keyword.value) {
						query += `&k=${keyword.value}`;
					}
				}
				for (let value of filterableProperties.values()) {
					initialParams.getAll(value.singularName).forEach(type => query += `&${value.queryName}=${type}`);
					initialParams.getAll(value.queryName).forEach(type => query += `&${value.queryName}=${type}`);
				}
			} else {
				if (keyword?.value) {
					query += `&k=${keyword.value}`;
				}
				if (keyword?.value === lastKeyword) {
					let inputs = container.querySelectorAll<HTMLInputElement>(`.filterableThingToggles input:checked,#${targetId}`);
					for (let input of inputs) {
						if (!input.value || !(input.id === targetId ? input.checked : true)) {
							continue;
						}

						if (!targetElement) {
							query += `&${input.name}=${input.value}`;
							continue;
						}

						const targetThingType = targetElement.getAttribute('data-thing-type');
						const inputThingType = input.getAttribute('data-thing-type');
						if (targetThingType && inputThingType && filterablePropertyNames.indexOf(targetThingType) >= filterablePropertyNames.indexOf(inputThingType)) {
							query += `&${input.name}=${input.value}`;
						}
					}
				}
				window.history.replaceState(null, '', window.location.pathname + (query ? '?' + query.substring(1) : ''));
			}

			const jobCount = container.querySelector<HTMLElement>(".job-count");
			if (jobCount !== null) {
				jobCount.innerHTML = `(${loadingHTML})`;
			}
			const jobList = container.querySelector<HTMLElement>(".job-container ul.jobs");
			if (jobList !== null) {
				jobList.innerHTML = loadingHTML;
			}

			const loadMore = container.querySelector<HTMLElement>(".load-more");
			if (loadMore !== null) {
				loadMore.style.display = "none";
				loadMore.style.opacity = "0";
			}

			xmlHttp.open("GET", url + (query ? '?' + query.substring(1) : ''), true);
			xmlHttp.send();
		}
	});
});
